import { useEffect, useState, type CSSProperties } from 'react';
import { CSS_EASE, enterVars, loopVars, oppositeOf, restLook, type Phase, type PresetValue } from './animateModel';
import { SCENE_FACES_URL } from './recipeScenes';
import { CoverField, RecipeCover } from './RecipeCover';
import { tileUrl } from '../content/tileUrl';

/**
 * The Animate bar's little pictures: a move's tile and a recipe's cover (RecipeCover.tsx). A move's tile plays CSS keyframes in index.css (`anim-thumb-*`), each picture saying where its shape
 * starts through custom properties — so forty of them cost one set of rules. Reduced
 * motion stops them all on the settled frame.
 */

/** How long one arrival-and-hold takes in a thumbnail. */
const CYCLE = 2.4;

function moving(phase: Exclude<Phase, 'loop'>, motion: string, direction: string, ease: string, amount: number, delay = 0, reach?: number): CSSProperties {
  if (motion === 'none') return {};
  const vars = phase === 'out' ? enterVars(motion, oppositeOf(direction), amount, reach) : enterVars(motion, direction, amount, reach);
  const timing = motion === 'typewriter' ? 'steps(6)' : (CSS_EASE[ease] ?? CSS_EASE.smooth);
  return { ...(vars as CSSProperties), animation: `anim-thumb-${phase} ${CYCLE}s ${timing} ${delay.toFixed(2)}s infinite both` };
}

function looping(motion: string, amount: number, delay = 0): CSSProperties {
  const { vars, cycle, spin, held } = loopVars(motion, amount);
  if (spin) return { animation: `anim-thumb-turn ${cycle}s linear ${delay.toFixed(2)}s infinite` };
  // Held poses for the loops that jump rather than glide: a jitter, a blink.
  return { ...(vars as CSSProperties), animation: `anim-thumb-loop ${cycle / 2}s ${held ? 'steps(1, jump-none)' : 'ease-in-out'} ${delay.toFixed(2)}s infinite alternate` };
}

/** What a move's tile draws the move ON: the layer that was picked, as near as a tile can show it. */
export type LayerPicture =
  /** Type: two letters say "this is your text" better than a line of it nobody can read at this size. */
  | { readonly kind: 'text' }
  /** The layer itself, drawn small. */
  | { readonly kind: 'picture'; readonly src: string }
  /** Anything that could not be drawn. */
  | { readonly kind: 'shape' };

function Pictured({ picture }: { picture: LayerPicture }) {
  if (picture.kind === 'text') return <span className="block px-[1px] text-[30px] font-semibold leading-none tracking-[-0.02em]">Aa</span>;
  if (picture.kind === 'picture') return <img src={tileUrl(picture.src, 128)} alt="" draggable={false} className="block max-h-[44px] max-w-[62px]" />;
  return <span className="block size-[28px] rounded-[5px] bg-current" />;
}

/**
 * One move to choose, for the layer that is picked: the layer itself where it rests, a
 * dashed outline where the move starts it from, and the move's name under it.
 *
 * At rest so ten tiles are ten pictures, not one picture caught at ten moments; pointing at
 * one — or reaching it with the keyboard — plays it in the tile and asks for it to be tried
 * on the page (`onTry`), and choosing it writes it. They used to be grey chips on a grid,
 * every one the same square, in a panel beside four dropdowns.
 */
export function MoveTile({
  phase,
  motion,
  name,
  direction,
  ease,
  picture,
  on,
  onPick,
  onTry,
}: {
  phase: Phase;
  motion: PresetValue;
  name: string;
  direction: string;
  ease: string;
  picture: LayerPicture;
  on: boolean;
  onPick: () => void;
  /** Pointed at, or left. */
  onTry: (trying: boolean) => void;
}) {
  const look = restLook(phase, motion, direction);
  const { animation, ...vars } = motion === 'none' ? ({} as CSSProperties) : phase === 'loop' ? looping(motion, 1.3) : moving(phase, motion, direction, ease, 1, 0, 15);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      onClick={onPick}
      onPointerEnter={() => onTry(true)}
      onPointerLeave={() => onTry(false)}
      onFocus={(event) => {
        if (event.currentTarget.matches(':focus-visible')) onTry(true);
      }}
      onBlur={() => onTry(false)}
      className="preset-tile group flex w-[132px] shrink-0 flex-col gap-[6px] rounded-[11px] text-left"
    >
      <span className={`block rounded-[11px] p-[2px] transition-shadow ${on ? 'shadow-[0_0_0_2px_var(--accent)]' : 'shadow-[0_0_0_1px_var(--separator)] group-hover:shadow-[0_0_0_1px_var(--label-3)]'}`}>
        <CoverField id={`move-${motion}`} graphite={motion === 'none'} className="rc-move">
          <span className="rc-ctr">
            {motion === 'none' ? (
              <span className="rc-none"><span /></span>
            ) : (
              <span className="relative inline-grid place-items-center">
                {look.ghost !== null ? <span className="absolute inset-[-2px] rounded-[3px] border border-dashed border-current opacity-50" style={{ transform: look.ghost }} /> : null}
                <span className="preset-chip anim-shape inline-grid" style={{ ...vars, ['--play' as string]: animation }}>
                  <span className="inline-grid" style={look.picture as CSSProperties}>
                    <Pictured picture={picture} />
                  </span>
                </span>
              </span>
            )}
          </span>
        </CoverField>
      </span>
      <span className={`truncate px-[3px] text-[12px] ${on ? 'font-medium text-accent-text' : 'text-label-2 group-hover:text-label'}`}>{name}</span>
    </button>
  );
}

/** The cards' own faces, asked for once — when the first recipe card is drawn, not at start-up. */
let facesAsked = false;
function askForFaces(): void {
  if (facesAsked || typeof document === 'undefined') return;
  facesAsked = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = SCENE_FACES_URL;
  document.head.appendChild(link);
}

/**
 * One recipe to choose: its cover, and its name under it.
 *
 * The cover stands finished, so the covers along the strip are different pictures
 * rather than one picture caught at different moments. Pointing at one — or reaching it
 * with the keyboard — plays it, over and over with a rest between, for as long as you
 * stay; choosing it plays the real page. No tooltip: the cover playing is the explanation,
 * and a caption floating over it while it plays is in the way.
 */
export function RecipeTile({ id, name, on, onPick }: { id: string; name: string; on: boolean; onPick: () => void }) {
  const [playing, setPlaying] = useState(false);
  useEffect(askForFaces, []);
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onPick}
      onPointerEnter={() => setPlaying(true)}
      onPointerLeave={() => setPlaying(false)}
      onFocus={(event) => setPlaying(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setPlaying(false)}
      className="group flex w-full min-w-0 flex-col gap-[6px] rounded-[11px] text-left"
    >
      <span className={`block rounded-[11px] p-[2px] transition-shadow ${on ? 'shadow-[0_0_0_2px_var(--accent)]' : 'shadow-[0_0_0_1px_var(--separator)] group-hover:shadow-[0_0_0_1px_var(--label-3)]'}`}>
        <RecipeCover id={id} playing={playing} />
      </span>
      <span className={`truncate px-[3px] text-[12px] ${on ? 'font-medium text-accent-text' : 'text-label-2 group-hover:text-label'}`}>{name}</span>
    </button>
  );
}

/** An arrow pointing up, turned to a direction. */
export function Arrow({ angle }: { angle: number }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden style={{ transform: `rotate(${angle}deg)` }}>
      <path d="M7 11.5V2.8M3.4 6.3 7 2.7l3.6 3.6" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
